import { observer } from 'mobx-react';
import Box from '@mui/material/Box';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Button, Card, CardContent, Chip, Drawer, Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { NewLeadDrawer } from '../../components/NewLeadDrawer';
import { AppNavbar } from '../../components/AppNavbar';
import { PipelinesMenu } from '../../components/PipelinesMenu';
import './style.css';
import LoadingProgressStore from '../../store/LoadingProgressStore';
import LeadStore from '../../store/LeadStore';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enIN } from 'date-fns/locale';
import { toJS } from 'mobx';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const defaultTheme = createTheme({
  typography: {
    button: {
      textTransform: 'none',
    },
  },
});

const CustomButton = styled(Button)({
  backgroundColor: '#F07B0E',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#d67e2d',
  },
  width: 'fit-content',
  height: '34px',
});

const HeadetPaperTile = styled(Paper)({
  '&:hover': {
    cursor: 'pointer',
  },
});

const StatusTilePaper = styled(Paper)({
  // marginTop: '0px !important',
  padding: '10px',
  minWidth: 'ax-content',
  borderTopLeftRadius: '0px',
  borderTopRightRadius: '0px',
});

const SubNavBarCard = styled(Card)({
  top: '49px',
  position: 'fixed',
  width: '-webkit-fill-available',
  zIndex: '10',
  height: '45px',
  minHeight: '45px',
});

export const Status = observer(() => {
  const currentDate = new Date();
  const futureDate = new Date(currentDate);
  const navigate = useNavigate();
  futureDate.setDate(currentDate.getDate() + 14);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(futureDate);
  const [endDateMin, setEndDateMin] = useState(dayjs(currentDate));
  const [statusView, setStatusView] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleSelectedPipelineChanged = (pipeline) => {
    setSelectedPipeline(pipeline);
  };

  useEffect(() => {
    LoadingProgressStore.toggleIsLoading(true);
    LeadStore.getPipelines();
    document.querySelector('#status-body-container').style.minHeight = `${window.innerHeight - 95}px`;
    document.querySelector('#status-body-container').style.height = `${window.innerHeight - 95}px`;
  }, []);

  useEffect(() => {
    document.querySelector('#status-body-container').classList[statusView ? 'remove' : 'add']('lm-d-none');
  }, [statusView]);

  useEffect(() => {
    if (selectedStatus) {
      setTimeout(() => {
        const subStatusContainer = document?.querySelector('#sub-status-body-container');
        if (subStatusContainer) {
          subStatusContainer.style.minHeight = `${window.innerHeight - 90}px`;
          subStatusContainer.style.height = `${window.innerHeight - 90}px`;
        }
      }, 100);
    }
  }, [selectedStatus]);

  useEffect(() => {
    if (LeadStore.getPipelinesSuccess) {
      LoadingProgressStore.toggleIsLoading(false);
      LeadStore.getPipelines();
    }
  }, [LeadStore.getPipelinesSuccess]);

  useEffect(() => {
    selectedPipeline &&
      LeadStore.getLeadSummaryOnStatus(
        selectedPipeline.id,
        dayjs(startDate).format('YYYY-MM-DD'),
        dayjs(endDate).format('YYYY-MM-DD')
      );
  }, [selectedPipeline, startDate, endDate]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setEndDate(date);

    const minDate = date;
    setEndDateMin(dayjs(minDate));
  };

  const handleShowSubStatusView = (statusObj) => {
    setSelectedStatus(groupLeadsBySubStatus(statusObj));
    setStatusView(false);
  };

  const statusType = {
    success: 1,
    warning: 2,
    primary: 3,
  };

  const statusHeaderColorMapping = [
    {
      type: statusType.primary,
      borderColor: '#5FBEF3',
      backgroundColor: '#C2E9FF',
      className: 'primary-status',
    },
    {
      type: statusType.success,
      borderColor: '#2CB00B',
      backgroundColor: '#B7DFAD',
      className: 'success-status',
    },
    {
      type: statusType.warning,
      borderColor: '#F07B0E',
      backgroundColor: '#FFCD9F',
      className: 'warning-status',
    },
  ];

  const isEmpty = (value) => {
    return value === null || value === undefined || value === '';
  };

  const getStatusHeaderTile = (type, currentWorkFlowStatus) => {
    const colorMapping = statusHeaderColorMapping.find((item) => item.type === type);
    return (
      <HeadetPaperTile
        onClick={() => handleShowSubStatusView(toJS(currentWorkFlowStatus))}
        className={colorMapping.className}
        sx={{
          backgroundColor: `${colorMapping.backgroundColor}`,
          p: '10px',
          borderTop: `5px solid ${colorMapping.borderColor}`,
        }}
      >
        <Stack direction={'column'}>
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <Typography sx={{ fontSize: '14px', fontWeight: '800', alignSelf: 'start' }} component="p">
              {currentWorkFlowStatus.currentWorkFlowName}
            </Typography>
            <Typography
              sx={{
                width: '5px',
                height: '5px',
                backgroundColor: '#484646',
                borderRadius: '50%',
              }}
              component="span"
            ></Typography>
            <Stack direction={'row'} alignSelf={'end'}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: '700',
                  alignSelf: 'start',
                  mr: '4px',
                }}
                component="p"
              >
                {currentWorkFlowStatus.leadsResponseDtos.length}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                Lead(s)
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </HeadetPaperTile>
    );
  };

  const getSubStatusHeaderTile = (type, currentWorkFlowSubStatus) => {
    const colorMapping = statusHeaderColorMapping.find((item) => item.type === type);
    return (
      <Paper
        sx={{
          backgroundColor: `${colorMapping.backgroundColor}`,
          p: '10px',
          borderTop: `5px solid ${colorMapping.borderColor}`,
        }}
      >
        <Stack direction={'column'}>
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <Typography sx={{ fontSize: '14px', fontWeight: '800', alignSelf: 'start' }} component="p">
              {currentWorkFlowSubStatus.workFlowSubStatusName}
            </Typography>
            <Typography
              sx={{
                width: '5px',
                height: '5px',
                backgroundColor: '#484646',
                borderRadius: '50%',
              }}
              component="span"
            ></Typography>
            <Stack direction={'row'} alignSelf={'end'}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: '700',
                  alignSelf: 'start',
                  mr: '4px',
                }}
                component="p"
              >
                {currentWorkFlowSubStatus.leads.length}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                Lead(s)
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const getStatusLeadsListTemplate = (list) => {
    return list.map((lead, leadIndex) => {
      return getLeadTile(lead, leadIndex);
    });
  };

  const groupLeadsBySubStatus = (statusObj) => {
    const groupedLeads = [];
    const subStatusIdList = statusObj.leadsResponseDtos
      .sort((a, b) => a.workFlowSubStatusId - b.workFlowSubStatusId)
      .map((lead) => {
        return {
          workFlowSubStatusId: lead.workFlowSubStatusId,
          workFlowSubStatusName: lead.workFlowSubStatusName,
        };
      })
      .filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.workFlowSubStatusId === item.workFlowSubStatusId &&
              t.workFlowSubStatusName === item.workFlowSubStatusName
          )
      );
    subStatusIdList.forEach((item) => {
      groupedLeads.push({
        ...item,
        leads: statusObj.leadsResponseDtos.filter((lead) => lead.workFlowSubStatusId === item.workFlowSubStatusId),
      });
    });

    return groupedLeads;
  };

  const getLeadTile = (lead, leadIndex) => {
    console.log("🚀 ~ getLeadTile ~ lead:", lead)
    return (
      <>
        <StatusTilePaper key={leadIndex}>
          <Stack direction={'row'} spacing={2}>
            <Stack spacing={1} sx={{ color: '#4A3011' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                Client Name
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                Phone number
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                Project stage
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                Priority
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                Lead Owner
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                :
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                :
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                :
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                :
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '700', alignSelf: 'start' }} component="p">
                :
              </Typography>
            </Stack>
            <Stack spacing={1}>
            <Typography
              sx={{ fontSize: '12px', fontWeight: '600', alignSelf: 'start', textDecoration: 'underline', cursor: 'pointer' }}
              component="p"
              onClick={() => navigate(`/home?pId=${lead.pipeLinesId}&leadId=${lead.id}`)}
            >
              {isEmpty(lead.customerName) ? '-' : lead.customerName}
            </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '600', alignSelf: 'start' }} component="p">
                {isEmpty(lead.contactNo) ? '-' : lead.contactNo}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '600', alignSelf: 'start' }} component="p">
                {isEmpty(lead.currentWorkflowName) ? '-' : lead.currentWorkflowName}
              </Typography>
              <Typography sx={{ fontSize: '12px', fontWeight: '600', alignSelf: 'start' }} component="p">
                {isEmpty(lead.leadPriority) ? '-' : lead.leadPriority}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: '600',
                  alignSelf: 'start',
                  color: '#FF5107',
                }}
                component="p"
              >
                {isEmpty(lead.leadHandledBy) ? '-' : lead.leadHandledBy}
              </Typography>
            </Stack>
          </Stack>
        </StatusTilePaper>
      </>
    );
  };

  const getStatusContainer = () => {
    return (
      <Box
        sx={{
          boxShadow: 'unset',
          overflow: 'auto',
          display: `${statusView ? '' : 'lm-d-none'}`,
        }}
      >
        <Stack direction="column">
          <SubNavBarCard>
            <CardContent sx={{ pt: '6px !important', pb: '6px !important' }}>
              <Stack justifyContent="space-between" direction="row" spacing={2}>
                <Stack id="status-stack-filter" spacing={1} direction={'row'}>
                  <PipelinesMenu handleSelectedPipelineChanged={handleSelectedPipelineChanged}></PipelinesMenu>
                </Stack>
                <Stack justifyContent="flex-end" direction="row" spacing={2}>
                  <CustomButton onClick={toggleDrawer} variant="contained">
                    Create Lead
                  </CustomButton>
                </Stack>
              </Stack>
            </CardContent>
          </SubNavBarCard>
          <Box
            id="status-body-container"
            flexGrow={1}
            overflow={'auto'}
            sx={{
              top: '95px',
              position: 'absolute',
              width: '-webkit-fill-available',
              p: '25px',
              boxShadow: 'unset',
              backgroundColor: '#ecf0f4',
            }}
          >
            <Stack direction={'row'} spacing={2} sx={{ pb: '20px' }} justifyContent={'flex-start'}>
              <div id="status-filter-start-date">
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                  <DatePicker
                    label="From date"
                    sx={{ width: 155 }}
                    value={startDate ? new Date(startDate) : null}
                    onChange={(newValue) => {
                      handleStartDateChange(newValue);
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </LocalizationProvider>
              </div>
              <div id="status-filter-end-date">
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                  <DatePicker
                    label="To date"
                    sx={{ width: 155 }}
                    minDate={endDateMin}
                    value={endDate ? new Date(endDate) : null}
                    disabled={startDate === null}
                    onChange={(newValue) => {
                      setEndDate(newValue);
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </LocalizationProvider>
              </div>
            </Stack>
            <Grid container columnGap={3} rowGap={3} wrap="nowrap">
              {LeadStore.leadSummaryByStatus.map((item, index) => {
                const sequence = index + 1;
                let statusTypeLocal = null;
                if (sequence > 1) {
                  if (sequence === LeadStore.leadSummaryByStatus.length) {
                    statusTypeLocal = statusType.success;
                  } else if (sequence === LeadStore.leadSummaryByStatus.length - 1) {
                    statusTypeLocal = statusType.warning;
                  } else {
                    statusTypeLocal = statusType.primary;
                  }
                } else {
                  statusTypeLocal = statusType.primary;
                }

                return (
                  <Stack key={index} direction={'column'} justifyContent={'stretch'} spacing={1}>
                    {getStatusHeaderTile(statusTypeLocal, item)}
                    {getStatusLeadsListTemplate(toJS(item.leadsResponseDtos))}
                  </Stack>
                );
              })}
            </Grid>
          </Box>
        </Stack>
      </Box>
    );
  };

  const getSubStatusContainer = () => {
    if (statusView) {
      return <></>;
    }

    return (
      <Box sx={{ boxShadow: 'unset', overflow: 'auto' }}>
        <Stack direction="column">
          <SubNavBarCard>
            <CardContent sx={{ pt: '6px !important', pb: '6px !important' }}>
              <Stack justifyContent="space-between" direction="row" spacing={2}>
                <Stack spacing={1} direction={'row'}>
                  <IconButton aria-label="back" size="small" onClick={() => setStatusView(true)}>
                    <ArrowBack />
                  </IconButton>
                </Stack>
              </Stack>
            </CardContent>
          </SubNavBarCard>
          <Box
            id="sub-status-body-container"
            flexGrow={1}
            overflow={'auto'}
            sx={{
              top: '95px',
              position: 'absolute',
              width: '-webkit-fill-available',
              p: '25px',
              boxShadow: 'unset',
              backgroundColor: '#ecf0f4',
            }}
          >
            <Grid container columnGap={3} rowGap={3} wrap="nowrap">
              {selectedStatus.map((item, index) => {
                const sequence = index + 1;
                let statusTypeLocal = null;
                if (sequence > 1) {
                  if (sequence === selectedStatus.length) {
                    statusTypeLocal = statusType.success;
                  } else if (sequence === selectedStatus.length - 1) {
                    statusTypeLocal = statusType.warning;
                  } else {
                    statusTypeLocal = statusType.primary;
                  }
                } else {
                  statusTypeLocal = statusType.primary;
                }

                return (
                  <Stack key={index} direction={'column'} justifyContent={'stretch'} spacing={1}>
                    {getSubStatusHeaderTile(statusTypeLocal, item)}
                    {getStatusLeadsListTemplate(item.leads)}
                  </Stack>
                );
              })}
            </Grid>
          </Box>
        </Stack>
      </Box>
    );
  };

  return (
    // <ThemeProvider theme={defaultTheme}>
    <Card>
      <AppNavbar></AppNavbar>
      {getStatusContainer()}
      {getSubStatusContainer()}
      <NewLeadDrawer open={drawerOpen} toggleDrawer={toggleDrawer}></NewLeadDrawer>
    </Card>
    // </ThemeProvider>
  );
});
